import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileUp, FlaskConical, Leaf, Loader2, Plus, Sparkles, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import Button from '../../components/ui/Button';
import { Card, CardContent } from '../../components/ui/Card';
import orchardService from '../../services/orchard';
import soilService from '../../services/soil';
import { errorMessage } from '../../utils/apiResponse';
import { getTodayInTz, useOrgTimezone } from '../../utils/timezone';
import { GROUPS, GROUP_LABEL, OTHER, SAMPLE_TYPE_LABEL, SOIL_ANALYTES, analytesOf } from '../../utils/soilAnalytes';
import { cn } from '../../utils/cn';
import type { Block, CultivarArea } from '../../types/orchard';
import type { SampleType, SoilProposal, SoilProposalSample, SoilReport, SoilResult, SoilSampleFormData, SoilSource } from '../../types/soil';

interface OtherDraft { key: string; label: string; value: string; unit: string }

interface Draft {
  sample_type: SampleType;
  tissue: string;
  growth_stage: string;
  block_id: string;
  cultivar_area_id: string;
  sampled_on: string;
  lab_name: string;
  lab_reference: string;
  sample_label: string;
  depth_from_cm: string;
  depth_to_cm: string;
  texture: string;
  notes: string;
  values: Record<string, string>;
  others: OtherDraft[];
}

let seq = 0;
const newKey = () => `o${(seq += 1)}`;
const numOrNull = (s: string) => (s.trim() === '' || !Number.isFinite(Number(s)) ? null : Number(s));
const asText = (r: Pick<SoilResult, 'value' | 'qualifier'>) => `${r.qualifier ?? ''}${r.value}`;
const VALUE_RE = /^[<>]?\s*-?\d+(\.\d+)?$/;

const inputCls = 'w-full rounded-lg border border-neutral-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500';

function resultsInto(d: Draft, results: SoilResult[]): Draft {
  const values: Record<string, string> = {};
  const others: OtherDraft[] = [];
  for (const r of results) {
    if (r.analyte === OTHER || !SOIL_ANALYTES.some((a) => a.key === r.analyte)) {
      others.push({ key: newKey(), label: r.label ?? '', value: asText(r), unit: r.unit ?? '' });
    } else values[r.analyte] = asText(r);
  }
  return { ...d, values, others };
}

/**
 * A soil sample: where it was taken, which lab, what depth, and its results.
 * A lab report (PDF, photo or CSV) can be uploaded first: it is kept with the
 * sample and read into the form, by rule for a CSV and by AI otherwise. What
 * is read is a proposal to check against the report; nothing is saved until
 * Save. A report holding several samples is entered one at a time, each in
 * its own block.
 */
export default function SoilSampleForm() {
  const { id } = useParams();
  const editingId = id ? Number(id) : null;
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const timezone = useOrgTimezone();

  const [blocks, setBlocks] = useState<Block[]>([]);
  const [areas, setAreas] = useState<CultivarArea[]>([]);
  const [draft, setDraft] = useState<Draft>(() => ({
    sample_type: params.get('type') === 'leaf' ? 'leaf' : 'soil',
    tissue: '',
    growth_stage: '',
    block_id: params.get('block_id') ?? '',
    cultivar_area_id: params.get('cultivar_area_id') ?? '',
    sampled_on: getTodayInTz(timezone),
    lab_name: '', lab_reference: '', sample_label: '', depth_from_cm: '0', depth_to_cm: '10', texture: '', notes: '',
    values: {}, others: [],
  }));
  const [loading, setLoading] = useState(!!editingId);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [report, setReport] = useState<SoilReport | null>(null);
  const [source, setSource] = useState<SoilSource>('manual');
  const [proposal, setProposal] = useState<SoilProposal | null>(null);
  const [proposalIndex, setProposalIndex] = useState(0);
  const [entered, setEntered] = useState<number[]>([]);
  const [reading, setReading] = useState(false);
  const [readError, setReadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    Promise.all([orchardService.getAllBlocks(), orchardService.getAllCultivarAreas()])
      .then(([b, a]) => {
        if (b.success && b.data) setBlocks(b.data.blocks);
        if (a.success && a.data) setAreas(a.data.areas);
      })
      .catch((e) => setError(errorMessage(e, 'Could not load the blocks')));
  }, []);

  useEffect(() => {
    if (!editingId) return;
    soilService.get(editingId)
      .then((res) => {
        if (!res.success || !res.data) return;
        const s = res.data;
        setDraft((d) => resultsInto({
          ...d,
          sample_type: s.sample_type ?? 'soil',
          tissue: s.tissue ?? '',
          growth_stage: s.growth_stage ?? '',
          block_id: String(s.block_id),
          cultivar_area_id: s.cultivar_area_id ? String(s.cultivar_area_id) : '',
          sampled_on: s.sampled_on,
          lab_name: s.lab_name ?? '', lab_reference: s.lab_reference ?? '', sample_label: s.sample_label ?? '',
          depth_from_cm: s.depth_from_cm != null ? String(s.depth_from_cm) : '',
          depth_to_cm: s.depth_to_cm != null ? String(s.depth_to_cm) : '',
          texture: s.texture ?? '', notes: s.notes ?? '',
        }, s.results));
        setSource(s.source);
        if (s.report_id) {
          setReport({ id: s.report_id, file_name: s.report_file_name ?? 'Report', mime_type: s.report_mime_type, size_bytes: null, created_at: s.created_at });
        }
      })
      .catch((e) => setError(errorMessage(e, 'Could not load the sample')))
      .finally(() => setLoading(false));
  }, [editingId]);

  const blockAreas = useMemo(() => areas.filter((a) => String(a.block_id) === draft.block_id), [areas, draft.block_id]);
  const sortedBlocks = useMemo(
    () => [...blocks].sort((a, b) => `${a.farm_name ?? ''} ${a.name}`.localeCompare(`${b.farm_name ?? ''} ${b.name}`)),
    [blocks],
  );

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const setValue = (key: string, v: string) => setDraft((d) => ({ ...d, values: { ...d.values, [key]: v } }));

  const applyProposalSample = (p: SoilProposal, i: number) => {
    const s: SoilProposalSample | undefined = p.samples[i];
    if (!s) return;
    setProposalIndex(i);
    setDraft((d) => resultsInto({
      ...d,
      lab_name: p.lab_name ?? d.lab_name,
      lab_reference: s.lab_reference ?? p.lab_reference ?? d.lab_reference,
      sample_label: s.sample_label ?? '',
      sampled_on: s.sampled_on ?? d.sampled_on,
      depth_from_cm: s.depth_from_cm != null ? String(s.depth_from_cm) : d.depth_from_cm,
      depth_to_cm: s.depth_to_cm != null ? String(s.depth_to_cm) : d.depth_to_cm,
      texture: s.texture ?? d.texture,
      tissue: s.tissue ?? d.tissue,
      growth_stage: s.growth_stage ?? d.growth_stage,
    }, s.results));
  };

  const upload = async (file: File) => {
    setReading(true);
    setReadError(null);
    setError(null);
    try {
      const res = await soilService.uploadReport(file, draft.sample_type);
      if (!res.success || !res.data) throw res;
      setReport(res.data.report);
      setReadError(res.data.read_error);
      const p = res.data.proposal;
      setProposal(p);
      setEntered([]);
      if (p && p.samples.length) {
        setSource(p.method);
        applyProposalSample(p, 0);
      }
    } catch (e) {
      setError(errorMessage(e, 'The report could not be uploaded'));
    } finally {
      setReading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const typeAnalytes = analytesOf(draft.sample_type);
  const leaf = draft.sample_type === 'leaf';

  const results = (): SoilSampleFormData['results'] => [
    ...typeAnalytes
      .filter((a) => (draft.values[a.key] ?? '').trim() !== '')
      .map((a) => ({ analyte: a.key, label: a.label, value: draft.values[a.key].trim(), qualifier: null, unit: a.unit || null, method: null })),
    ...draft.others
      .filter((o) => o.label.trim() && o.value.trim())
      .map((o) => ({ analyte: OTHER, label: o.label.trim(), value: o.value.trim(), qualifier: null, unit: o.unit.trim() || null, method: null })),
  ];

  const badValues = [
    ...typeAnalytes.filter((a) => (draft.values[a.key] ?? '').trim() && !VALUE_RE.test(draft.values[a.key].trim())).map((a) => a.label),
    ...draft.others.filter((o) => o.value.trim() && !VALUE_RE.test(o.value.trim())).map((o) => o.label || 'Other result'),
  ];
  const remaining = proposal ? proposal.samples.map((_, i) => i).filter((i) => i !== proposalIndex && !entered.includes(i)) : [];

  const save = async (andNext: boolean) => {
    setError(null);
    if (!draft.block_id) { setError('Choose the block the sample was taken in.'); return; }
    if (badValues.length) { setError(`Not a number: ${badValues.join(', ')}. A detection limit is written like <0.5.`); return; }
    const res = results();
    if (!res.length) { setError('Enter at least one result.'); return; }
    const data: SoilSampleFormData = {
      block_id: Number(draft.block_id),
      cultivar_area_id: draft.cultivar_area_id ? Number(draft.cultivar_area_id) : null,
      sampled_on: draft.sampled_on,
      lab_name: draft.lab_name.trim() || null,
      lab_reference: draft.lab_reference.trim() || null,
      sample_label: draft.sample_label.trim() || null,
      depth_from_cm: leaf ? null : numOrNull(draft.depth_from_cm),
      depth_to_cm: leaf ? null : numOrNull(draft.depth_to_cm),
      texture: leaf ? null : draft.texture.trim() || null,
      notes: draft.notes.trim() || null,
      report_id: report?.id ?? null,
      source,
      sample_type: draft.sample_type,
      tissue: leaf ? draft.tissue.trim() || null : null,
      growth_stage: leaf ? draft.growth_stage.trim() || null : null,
      results: res,
    };
    setSaving(true);
    try {
      const saved = editingId ? await soilService.update(editingId, data) : await soilService.create(data);
      if (!saved.success || !saved.data) throw saved;
      if (andNext && proposal && remaining.length) {
        setEntered((e) => [...e, proposalIndex]);
        setDraft((d) => ({ ...d, block_id: '', cultivar_area_id: '' }));
        applyProposalSample(proposal, remaining[0]);
        window.scrollTo({ top: 0 });
      } else {
        navigate(`/orchard/soil/${saved.data.id}`, { replace: true });
      }
    } catch (e) {
      setError(errorMessage(e, 'Could not save the sample'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-neutral-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={editingId ? `${SAMPLE_TYPE_LABEL[draft.sample_type]} sample` : `New ${leaf ? 'leaf tissue' : 'soil'} sample`}
        subtitle="Results from the lab, for a block or one planting in it"
        icon={leaf ? <Leaf className="h-6 w-6 text-white" /> : <FlaskConical className="h-6 w-6 text-white" />}
        showBackButton
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {error && (
          <div className="bg-error-50 border border-error-200 rounded-lg p-4">
            <p className="text-error-700 text-sm">{error}</p>
          </div>
        )}

        {/* Soil or leaf */}
        <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Kind of sample">
          {(['soil', 'leaf'] as const).map((t) => (
            <button
              key={t}
              type="button"
              role="radio"
              aria-checked={draft.sample_type === t}
              disabled={!!proposal && draft.sample_type !== t}
              onClick={() => set('sample_type', t)}
              className={cn('px-3 py-1.5 rounded-full border text-sm inline-flex items-center gap-1.5 disabled:opacity-50',
                draft.sample_type === t ? 'bg-primary-50 border-primary-300 text-primary-800' : 'border-neutral-200 text-neutral-600 bg-white')}
            >
              {t === 'leaf' ? <Leaf className="h-4 w-4" /> : <FlaskConical className="h-4 w-4" />}
              {SAMPLE_TYPE_LABEL[t]}
            </button>
          ))}
        </div>

        {/* The report */}
        <Card>
          <CardContent className="p-5 space-y-3">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-neutral-700">Lab report</h3>
                <p className="text-xs text-neutral-500">
                  A PDF, a photo of the printed report, or the lab's CSV. It is kept with the sample, and its figures are read into the form below.
                </p>
              </div>
              <Button size="sm" variant="outline" isLoading={reading} onClick={() => fileRef.current?.click()}>
                <FileUp className="h-4 w-4" />
                {report ? 'Another report' : 'Upload report'}
              </Button>
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                accept=".pdf,.csv,.txt,image/jpeg,image/png,image/webp,image/gif"
                aria-label="Lab report file"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
              />
            </div>
            {reading && <p className="text-sm text-neutral-600">Reading the report. A PDF or photo can take a minute or two.</p>}
            {report && (
              <p className="text-sm text-neutral-700 break-words">
                Attached: <button type="button" className="text-primary-600 hover:underline break-all text-left" onClick={() => soilService.openReport(report.id, report.file_name)}>{report.file_name}</button>
                {' '}<button type="button" className="text-xs text-neutral-500 hover:underline" onClick={() => { setReport(null); setProposal(null); }}>remove</button>
              </p>
            )}
            {readError && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
                The report is attached but could not be read: {readError} Type the figures in below.
              </p>
            )}
            {proposal && proposal.samples.length > 0 && (
              <div className="rounded-lg border border-primary-200 bg-primary-50 p-3 space-y-2">
                <p className="text-sm text-primary-900 flex items-start gap-2">
                  <Sparkles className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>
                    {proposal.method === 'ai' ? 'Read by AI' : 'Read from the CSV'}: check every figure against the report before saving.
                    {proposal.samples.length > 1 && ` The report has ${proposal.samples.length} samples; enter them one at a time.`}
                  </span>
                </p>
                {proposal.samples.length > 1 && (
                  <label className="block text-sm">
                    <span className="text-neutral-700">Sample from the report</span>
                    <select
                      className={inputCls}
                      value={proposalIndex}
                      onChange={(e) => applyProposalSample(proposal, Number(e.target.value))}
                    >
                      {proposal.samples.map((s, i) => (
                        <option key={i} value={i}>
                          {s.sample_label || `Sample ${i + 1}`}{entered.includes(i) ? ' (saved)' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                )}
                {proposal.warnings.length > 0 && (
                  <ul className="text-xs text-amber-800 list-disc pl-5 space-y-0.5">
                    {proposal.warnings.map((w) => <li key={w}>{w}</li>)}
                  </ul>
                )}
              </div>
            )}
            {proposal && proposal.samples.length === 0 && (
              <ul className="text-sm text-amber-800 list-disc pl-5">
                {(proposal.warnings.length ? proposal.warnings : ['Nothing could be read from this report.']).map((w) => <li key={w}>{w}</li>)}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Where and when */}
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="text-sm font-semibold text-neutral-700">Where and when</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <label className="block text-sm">
                <span className="text-neutral-700">Block</span>
                <select className={inputCls} value={draft.block_id}
                  onChange={(e) => setDraft((d) => ({ ...d, block_id: e.target.value, cultivar_area_id: '' }))}>
                  <option value="">Choose a block</option>
                  {sortedBlocks.map((b) => (
                    <option key={b.id} value={b.id}>{b.farm_name ? `${b.farm_name} › ` : ''}{b.name}</option>
                  ))}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-neutral-700">Cultivar area</span>
                <select className={inputCls} value={draft.cultivar_area_id} disabled={!draft.block_id}
                  onChange={(e) => set('cultivar_area_id', e.target.value)}>
                  <option value="">The whole block</option>
                  {blockAreas.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </label>
              <label className="block text-sm">
                <span className="text-neutral-700">Date sampled</span>
                <input type="date" className={inputCls} value={draft.sampled_on} onChange={(e) => set('sampled_on', e.target.value)} />
              </label>
              {leaf ? (
                <label className="block text-sm">
                  <span className="text-neutral-700">Growth stage</span>
                  <input type="text" className={inputCls} placeholder="e.g. Mid-season, 100 days after full bloom" value={draft.growth_stage} onChange={(e) => set('growth_stage', e.target.value)} />
                </label>
              ) : (
              <div className="grid grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-neutral-700">Depth from (cm)</span>
                  <input type="text" inputMode="decimal" className={inputCls} value={draft.depth_from_cm} onChange={(e) => set('depth_from_cm', e.target.value)} />
                </label>
                <label className="block text-sm">
                  <span className="text-neutral-700">Depth to (cm)</span>
                  <input type="text" inputMode="decimal" className={inputCls} value={draft.depth_to_cm} onChange={(e) => set('depth_to_cm', e.target.value)} />
                </label>
              </div>
              )}
              <label className="block text-sm">
                <span className="text-neutral-700">Laboratory</span>
                <input type="text" className={inputCls} value={draft.lab_name} onChange={(e) => set('lab_name', e.target.value)} />
              </label>
              <label className="block text-sm">
                <span className="text-neutral-700">Lab reference</span>
                <input type="text" className={inputCls} value={draft.lab_reference} onChange={(e) => set('lab_reference', e.target.value)} />
              </label>
              <label className="block text-sm">
                <span className="text-neutral-700">Sample name on the report</span>
                <input type="text" className={inputCls} value={draft.sample_label} onChange={(e) => set('sample_label', e.target.value)} />
              </label>
              {leaf ? (
                <label className="block text-sm">
                  <span className="text-neutral-700">Tissue sampled</span>
                  <input type="text" className={inputCls} placeholder="e.g. Mid-shoot leaves, current season" value={draft.tissue} onChange={(e) => set('tissue', e.target.value)} />
                </label>
              ) : (
                <label className="block text-sm">
                  <span className="text-neutral-700">Texture</span>
                  <input type="text" className={inputCls} placeholder="e.g. Sandy loam" value={draft.texture} onChange={(e) => set('texture', e.target.value)} />
                </label>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Results */}
        <Card>
          <CardContent className="p-5 space-y-5">
            <div>
              <h3 className="text-sm font-semibold text-neutral-700">Results</h3>
              <p className="text-xs text-neutral-500">Fill in what the lab measured and leave the rest blank. A figure below detection is written like &lt;0.5.</p>
            </div>
            {GROUPS[draft.sample_type].map((g) => (
              <section key={g} className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">{GROUP_LABEL[g]}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                  {typeAnalytes.filter((a) => a.group === g).map((a) => (
                    <label key={a.key} className="block text-sm">
                      <span className="text-neutral-700">{a.label}{a.unit && <span className="text-neutral-500"> ({a.unit})</span>}</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        className={inputCls}
                        value={draft.values[a.key] ?? ''}
                        onChange={(e) => setValue(a.key, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
              </section>
            ))}

            <section className="space-y-2">
              <h4 className="text-xs font-semibold uppercase tracking-wide text-neutral-500">Other results</h4>
              {draft.others.map((o) => (
                <div key={o.key} className="grid grid-cols-[1fr_auto] sm:grid-cols-[2fr_1fr_1fr_auto] gap-2 items-end">
                  <label className="block text-sm col-span-2 sm:col-span-1">
                    <span className="text-neutral-700">Name</span>
                    <input type="text" className={inputCls} value={o.label}
                      onChange={(e) => set('others', draft.others.map((x) => (x.key === o.key ? { ...x, label: e.target.value } : x)))} />
                  </label>
                  <label className="block text-sm">
                    <span className="text-neutral-700">Value</span>
                    <input type="text" inputMode="decimal" className={inputCls} value={o.value}
                      onChange={(e) => set('others', draft.others.map((x) => (x.key === o.key ? { ...x, value: e.target.value } : x)))} />
                  </label>
                  <label className="block text-sm">
                    <span className="text-neutral-700">Unit</span>
                    <input type="text" className={inputCls} value={o.unit}
                      onChange={(e) => set('others', draft.others.map((x) => (x.key === o.key ? { ...x, unit: e.target.value } : x)))} />
                  </label>
                  <button type="button" aria-label={`Remove ${o.label || 'result'}`} className="p-2 text-neutral-400 hover:text-error-600"
                    onClick={() => set('others', draft.others.filter((x) => x.key !== o.key))}>
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <Button size="sm" variant="ghost" onClick={() => set('others', [...draft.others, { key: newKey(), label: '', value: '', unit: '' }])}>
                <Plus className="h-4 w-4" />
                Add a result
              </Button>
            </section>

            <label className="block text-sm">
              <span className="text-neutral-700">Notes</span>
              <textarea className={inputCls} rows={3} value={draft.notes} onChange={(e) => set('notes', e.target.value)} />
            </label>
          </CardContent>
        </Card>

        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          {!editingId && remaining.length > 0 && (
            <Button variant="outline" isLoading={saving} onClick={() => save(true)}>Save and next sample</Button>
          )}
          <Button variant="primary" isLoading={saving} onClick={() => save(false)}>Save</Button>
        </div>
      </div>
    </div>
  );
}
